import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/src/lib/supabase';

export interface AuthState {
  session: Session | null;
  loading: boolean;
}

export function useAuth(): AuthState & {
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, name: string, captchaToken?: string) => Promise<{ error: string | null; needsConfirmation: boolean }>;
  signOut: () => Promise<void>;
} {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    const sub = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      cancelled = true;
      sub.data.subscription.unsubscribe();
    };
  }, []);

  return {
    session,
    loading,
    async signIn(email, password, captchaToken) {
      const { error } = await supabase.auth.signInWithPassword({ email, password, options: captchaToken ? { captchaToken } : undefined });
      return { error: error?.message ?? null };
    },
    async signUp(email, password, name, captchaToken) {
      // Send the confirmation link back to wherever they started, so an invite
      // (the ?invite=token in the URL) survives the email round-trip.
      const emailRedirectTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { name }, emailRedirectTo, captchaToken } });
      if (error) return { error: error.message, needsConfirmation: false };
      // No session back = email confirmation is required before sign-in.
      return { error: null, needsConfirmation: !data.session };
    },
    async signOut() {
      await supabase.auth.signOut();
    },
  };
}
